import { useState } from 'react';
import { isAxiosError } from 'axios';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { requestPaymentEvidence } from '../../api/payments';

/** Finance asks for a replacement or additional receipt (Phase 107). The
 * person who recorded the payment is notified and may upload once; the
 * request stays in the payment's audit history. */
export const RequestReceiptDialog = ({
  payment,
  onClose,
  onDone,
}: {
  payment: { id: number; paymentNumber: string } | null;
  onClose: () => void;
  onDone: () => void;
}) => {
  const [kind, setKind] = useState<'REPLACEMENT' | 'ADDITIONAL'>('REPLACEMENT');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    if (busy) return;
    setKind('REPLACEMENT');
    setReason('');
    setError(null);
    onClose();
  };

  const submit = async () => {
    if (!payment) return;
    setBusy(true);
    setError(null);
    try {
      await requestPaymentEvidence(payment.id, { kind, reason: reason.trim() });
      onDone();
      setBusy(false);
      close();
    } catch (err) {
      setError(isAxiosError(err) ? (err.response?.data?.message ?? "Couldn't send the request") : "Couldn't send the request");
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!payment} onClose={close} fullWidth maxWidth="xs" aria-labelledby="request-receipt-title">
      <DialogTitle id="request-receipt-title">Request a receipt — {payment?.paymentNumber}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            The person who recorded this payment is notified and can upload once. Earlier receipts are kept.
          </Typography>
          <RadioGroup value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
            <FormControlLabel value="REPLACEMENT" control={<Radio />} label="Replacement receipt" />
            <FormControlLabel value="ADDITIONAL" control={<Radio />} label="Additional receipt" />
          </RadioGroup>
          <TextField
            label="What is needed"
            multiline
            minRows={2}
            fullWidth
            inputProps={{ maxLength: 500 }}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            helperText="The counselor sees this."
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" disabled={busy || reason.trim().length < 3} onClick={() => void submit()}>
          {busy ? 'Sending…' : 'Send request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
